import React, { ChangeEvent, useContext } from 'react'
import Checkbox from 'app/components/checkbox'
import TextInput from 'app/components/text-input'
import Switch from 'app/modules/reports/components/switch'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { SignalVerdict } from 'app/modules/reports/types'
import { getVerdictLabel, uniqueVerdicts } from 'app/modules/reports/helpers'
import { applicationConfiguration } from 'app/modules/shared'
import DropdownSelect from 'app/components/dropdown'

export interface IndicatorFiltersProps {
  verdictFilter: SignalVerdict[]
  onVerdictFilterChange: (verdict: SignalVerdict, checked: boolean) => void
  mimeFilter: string
  mimeTypes: string[]
  setMimeFilter: (mime: string) => void
  searchInput: string
  onSearchChange: (value: string) => void
}

export const IndicatorFilters: React.FC<Readonly<IndicatorFiltersProps>> = ({
  verdictFilter,
  onVerdictFilterChange,
  mimeFilter,
  mimeTypes,
  setMimeFilter,
  searchInput,
  onSearchChange,
}) => {
  const { _t } = useTypedTranslation()
  const { useHumanFriendlyVerdicts } = useContext(applicationConfiguration)

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget as HTMLInputElement
    onSearchChange(value)
  }

  return (
    <div className="threat-indicators-filter">
      <div className="d-block flex-grow-1">
        <p className="pb-1 m-0">{_t('threat-indicators-verdict')}</p>
        <div className="verdict-checkbox-container">
          {Object.values(uniqueVerdicts())
            .reverse()
            .map((verdict) => (
              <Checkbox
                key={verdict}
                onChange={(checked) => onVerdictFilterChange(verdict, checked)}
                checked={verdictFilter.includes(verdict)}
                id={`verdict-filter-${verdict}`}
                label={getVerdictLabel(verdict, useHumanFriendlyVerdicts)}
                variant={verdict}
              />
            ))}
        </div>
      </div>
      <div className="d-block flex-grow-1" style={{ flexBasis: 0 }}>
        <p className="pb-1 m-0">{_t('mime-type')}</p>
        <DropdownSelect
          id="mime-type"
          searchable
          value={mimeFilter}
          items={[{ value: '', name: _t('all') }, ...mimeTypes.map((type) => ({ value: type, name: type }))]}
          onClick={(value) => setMimeFilter(value)}
        />
      </div>
      <div className="w-100 d-block flex-grow-1">
        <p className="pb-1 m-0">{_t('search')}</p>
        <TextInput
          id="indicator-search"
          placeholder={_t('search-indicator-id')}
          value={searchInput}
          onChange={handleSearchChange}
          prefix={<i className="ds3-icon ds3-search" />}
          clearable
        />
      </div>
    </div>
  )
}

export interface IndicatorListHeaderProps {
  indicatorsCount: number
  allSelected: boolean
  onSelectAll: () => void
  onOpenAll: () => void
  value: boolean
}

export const IndicatorListHeader: React.FC<Readonly<IndicatorListHeaderProps>> = ({
  indicatorsCount,
  allSelected,
  onSelectAll,
  onOpenAll,
  value,
}) => {
  const { _t } = useTypedTranslation()

  return (
    <div className="indicators-list-header">
      <Checkbox
        labelClassName="text-m"
        label={`${_t('threat-indicators')} (${indicatorsCount})`}
        onChange={onSelectAll}
        checked={allSelected}
      />
      <Switch label={_t('open-all-categories')} enabled={value} onChange={onOpenAll} />
    </div>
  )
}
